import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Login } from './components/auth/Login';
import { Dashboard } from './components/dashboard/Dashboard';
import { ProductList, ProductForm } from './components/products';
import InstallationList from './components/installations/InstallationList';
import { BudgetListNew, BudgetFormNew, BudgetDetailNew, BudgetEditNew } from './components/budgets';
import { UserManagement, PriceListManagement, PriceListDetail, FreightManagement, ConditionsConfig, CountryConfig, PhotosMigration } from './components/admin';
import { Layout } from './components/layout';
import { MlOrderList } from './components/ml/MlOrderList';

// Tema personalizado
const theme = createTheme({
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          boxShadow: '0 4px 24px 0 rgba(60,72,100,0.10)',
          transition: 'box-shadow 0.3s cubic-bezier(.25,.8,.25,1)',
          padding: '8px 0',
          '&:hover': {
            boxShadow: '0 8px 32px 0 rgba(60,72,100,0.18)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '28px 32px 24px 32px',
          '@media (max-width:600px)': {
            padding: '18px 10px 16px 10px',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorSuccess: {
          backgroundColor: '#e0f2fe', // celeste muy claro
          color: '#0ea5e9', // celeste principal (info.main)
        },
        colorDefault: {
          backgroundColor: '#e5e7eb',
          color: '#64748b',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedSuccess: {
          backgroundColor: '#eafaf2',
          color: 'rgb(66,154,45)',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: '#d1f5e0',
            boxShadow: 'none',
          },
        },
        outlinedSuccess: {
          borderColor: '#eafaf2',
          color: 'rgb(66,154,45)',
          backgroundColor: '#f8fdfb',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: '#eafaf2',
            borderColor: 'rgb(66,154,45)',
            boxShadow: 'none',
          },
        },
      },
    },
  },
  palette: {
    primary: {
      main: 'rgb(66,154,45)', // Verde principal personalizado
      light: '#8fd19e', // Verde claro más suave
      dark: '#3a6b2b', // Verde oscuro más elegante
      contrastText: '#fff',
    },
    secondary: {
      main: '#3b82f6', // Azul moderno
      light: '#93c5fd',
      dark: '#1e40af',
      contrastText: '#fff',
    },
    success: {
      main: '#22c55e', // Verde éxito más vibrante
      light: '#86efac',
      dark: '#15803d',
      contrastText: '#fff',
    },
    error: {
      main: '#ef4444', // Rojo moderno
      light: '#fca5a5',
      dark: '#991b1b',
      contrastText: '#fff',
    },
    warning: {
      main: '#f59e42', // Naranja suave
      light: '#fde68a',
      dark: '#b45309',
      contrastText: '#fff',
    },
    info: {
      main: '#0ea5e9', // Celeste informativo
      light: '#7dd3fc',
      dark: '#0369a1',
      contrastText: '#fff',
    },
    background: {
      default: '#f4f6fb', // Fondo gris muy claro
      paper: '#fff', // Tarjetas blancas
    },
    divider: '#e0e7ef', // Divisor suave
    text: {
      primary: '#222e3a', // Gris oscuro para mejor contraste
      secondary: '#64748b', // Gris azulado para textos secundarios
    },
  },
  typography: {
    fontFamily: '"Lato", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

// Componente para rutas protegidas
// ml_operator solo puede estar en /ml (facturación Mercado Libre); cualquier
// otra ruta protegida lo manda ahí. mlAllowed marca esas rutas.
const ProtectedRoute: React.FC<{ children: React.ReactNode; mlAllowed?: boolean }> = ({ children, mlAllowed }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div>Cargando...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'ml_operator' && !mlAllowed) {
    return <Navigate to="/ml" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Componente para rutas protegidas de admin
const AdminProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div>Cargando...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/products" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Componente para rutas públicas (solo si no está autenticado)
//
// Deliberately does NOT gate on `isLoading` the way Protected/AdminProtectedRoute
// do. `isLoading` flips true the instant any login attempt starts (LOGIN_START),
// not just during the initial auth-restore check - gating here would unmount
// <Login> mid-attempt and remount it fresh once the request settles, discarding
// any of Login's own local state set in between. That's exactly what broke the
// Google dual-account picker (CLAUDE.md §1/§2): the credential callback sets
// `countryOptions` on the original (by-then-unmounted) instance, a silent no-op,
// so the remounted Login just shows its default form with no error and no
// picker - a fast unmount/remount flash and nothing else. Login already renders
// its own loading state from `isLoading` where it actually matters (button/spinner).
export const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();

  return !user ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

const AppContent: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* Rutas públicas */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />

        {/* Rutas protegidas */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />

        {/* Rutas de productos */}
        <Route 
          path="/products" 
          element={
            <ProtectedRoute>
              <ProductList />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/products/new" 
          element={
            <AdminProtectedRoute>
              <ProductForm />
            </AdminProtectedRoute>
          } 
        />
        <Route 
          path="/products/:id/edit" 
          element={
            <AdminProtectedRoute>
              <ProductForm />
            </AdminProtectedRoute>
          } 
        />

        {/* Rutas de instalaciones */}
        <Route 
          path="/installations" 
          element={
            <ProtectedRoute>
              <InstallationList />
            </ProtectedRoute>
          } 
        />

        {/* Rutas de presupuestos */}
        <Route 
          path="/budgets" 
          element={
            <ProtectedRoute>
              <BudgetListNew />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/budgets/new" 
          element={
            <ProtectedRoute>
              <BudgetFormNew />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/budgets/:id/edit" 
          element={
            <ProtectedRoute>
              <BudgetEditNew />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/budgets/:id" 
          element={
            <ProtectedRoute>
              <BudgetDetailNew />
            </ProtectedRoute>
          } 
        />

        {/* Rutas de administración */}
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/price-lists"
          element={
            <ProtectedRoute>
              <PriceListManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/price-lists/:id"
          element={
            <ProtectedRoute>
              <PriceListDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/fletes"
          element={
            <ProtectedRoute>
              <FreightManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/condiciones"
          element={
            <ProtectedRoute>
              <ConditionsConfig />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/configuracion-pais"
          element={
            <ProtectedRoute>
              <CountryConfig />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/fotos"
          element={
            <ProtectedRoute>
              <PhotosMigration />
            </ProtectedRoute>
          }
        />

        {/* Facturación Mercado Libre (admin + ml_operator; también recibe el
            retorno OAuth ?code=&state=) */}
        <Route
          path="/ml"
          element={
            <ProtectedRoute mlAllowed>
              <MlOrderList />
            </ProtectedRoute>
          }
        />

        {/* Redirección por defecto */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        
        {/* Ruta catch-all */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
